<template>
  <!-- Issue #30 — Vista previa de informe de notas (tarea 8.6) -->
  <teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="visible"
        class="preview-backdrop"
        role="dialog"
        aria-modal="true"
        aria-label="Vista previa del informe de notas"
        @click.self="$emit('close')"
      >
        <div class="preview-modal">
          <!-- Cabecera -->
          <div class="pv-header">
            <div>
              <h2 class="pv-title">Vista previa — Notas al Estado Financiero</h2>
              <p class="pv-subtitle">Así aparecerán en el informe PDF final.</p>
            </div>
            <button
              type="button"
              class="pv-close"
              aria-label="Cerrar vista previa"
              @click="$emit('close')"
            >
              <i class="pi pi-times" aria-hidden="true" />
            </button>
          </div>

          <!-- Documento simulado -->
          <div class="pv-document" aria-label="Documento simulado">
            <!-- Membrete -->
            <div class="doc-letterhead">
              <div class="doc-logo" aria-hidden="true">🏢</div>
              <div>
                <p class="doc-company">Nombre de la Empresa S.A.S.</p>
                <p class="doc-period">Notas a los Estados Financieros</p>
                <p class="doc-date">Al 31 de diciembre de {{ currentYear }}</p>
              </div>
            </div>

            <hr class="doc-divider" />

            <!-- Lista de notas -->
            <div v-if="notes.length" class="doc-notes-list">
              <div
                v-for="(note, i) in sortedNotes"
                :key="note.id ?? i"
                class="doc-note"
              >
                <h3 class="doc-note-title">
                  Nota {{ note.note_number ?? (i + 1) }}. {{ note.title }}
                </h3>
                <p
                  v-if="note.norm_reference"
                  class="doc-note-norm"
                >
                  Ref. {{ note.norm_reference }}
                </p>
                <p class="doc-note-body">{{ note.content }}</p>
              </div>
            </div>

            <div v-else class="doc-empty">
              <p>No hay notas creadas todavía. Agrega notas para ver la vista previa.</p>
            </div>

            <!-- Pie -->
            <div class="doc-footer">
              <p>Representante Legal: _________________________</p>
              <p>Contador Público: _________________________</p>
            </div>
          </div>

          <!-- Acciones -->
          <div class="pv-actions">
            <button type="button" class="btn-close-pv" @click="$emit('close')">
              <i class="pi pi-times" aria-hidden="true" /> Cerrar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Note {
  id?: number | string
  note_number?: number
  title: string
  content: string
  norm_reference?: string
  [key: string]: unknown
}

const props = defineProps<{
  visible: boolean
  notes: Note[]
}>()

defineEmits<{ close: [] }>()

const currentYear = new Date().getFullYear()

const sortedNotes = computed(() =>
  [...props.notes].sort((a, b) => (a.note_number ?? 0) - (b.note_number ?? 0)),
)
</script>

<style scoped>
.preview-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  z-index: 2000;
  overflow-y: auto;
}

.preview-modal {
  background: var(--p-surface-0, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Cabecera */
.pv-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
}

.pv-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: var(--p-text-color, #111827);
}

.pv-subtitle {
  margin: 0.2rem 0 0;
  font-size: 0.75rem;
  color: var(--p-text-muted-color, #6b7280);
}

.pv-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: var(--p-text-muted-color, #9ca3af);
  padding: 0.25rem;
  border-radius: 6px;
  flex-shrink: 0;
}
.pv-close:hover { color: var(--p-text-color, #111827); }

/* Documento */
.pv-document {
  padding: 2rem;
  overflow-y: auto;
  max-height: 60vh;
  font-family: 'Times New Roman', Times, serif;
  font-size: 0.875rem;
  color: #1a1a1a;
  background: #fefefe;
}

.doc-letterhead {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.doc-logo { font-size: 2.5rem; }

.doc-company {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.doc-period {
  margin: 0.2rem 0 0;
  font-size: 0.875rem;
  font-style: italic;
}

.doc-date {
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: #555;
}

.doc-divider {
  border: none;
  border-top: 2px solid #1a1a1a;
  margin: 1rem 0;
}

.doc-notes-list { display: flex; flex-direction: column; gap: 1.5rem; }

.doc-note-title {
  margin: 0 0 0.3rem;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: underline;
}

.doc-note-norm {
  margin: 0 0 0.3rem;
  font-size: 0.72rem;
  color: #777;
  font-style: italic;
}

.doc-note-body {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.7;
  white-space: pre-wrap;
  text-align: justify;
}

.doc-empty {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
  font-size: 0.85rem;
  font-family: system-ui, sans-serif;
}

.doc-footer {
  margin-top: 3rem;
  border-top: 1px solid #ccc;
  padding-top: 1rem;
  font-size: 0.75rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.doc-footer p { margin: 0; }

/* Acciones */
.pv-actions {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--p-surface-200, #e5e7eb);
}

.btn-close-pv {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 1.25rem;
  border: 1.5px solid var(--p-surface-300, #d1d5db);
  border-radius: 8px;
  background: none;
  color: var(--p-text-color, #374151);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-close-pv:hover { background: var(--p-surface-100, #f3f4f6); }

/* Transición */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

:root[data-theme='dark'] .preview-modal,
.dark .preview-modal {
  background: var(--p-surface-800, #1f2937);
}

:root[data-theme='dark'] .pv-document,
.dark .pv-document {
  background: #1a1a2e;
  color: #e5e7eb;
}
</style>
